import { ArrowRight, ListMusic } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router";

import { Button, EmptyState, PageHeader, PageSection, Skeleton, StatusMessage } from "@ceedero/ui";

import { useAccountProfile } from "@/account/hooks/use-account";
import { useCloudLibrary } from "@/cloud/hooks/use-cloud-pull";
import { AppPage } from "@/components/app-page";
import { useDevices } from "@/devices/hooks/use-devices";
import { LatestProject } from "@/home/components/latest-project";
import { LibraryLedger } from "@/home/components/library-ledger";
import { RecentProjectRow } from "@/home/components/recent-project-row";
import { useTranslation } from "@/localization/hooks/use-translation";
import {
  buildDuplicateGroups,
  collectCatalogTracks,
  countDuplicatesByProject,
  selectLiveProjects,
  sumTrackDuration,
} from "@/music-catalog/utils/catalog-view";
import { ROUTES } from "@/navigation/constants/routes";

/** The latest album in the window, and the eight before it in the index. */
const RECENT_PROJECT_COUNT = 9;

/**
 * Where signing in lands.
 *
 * Laid out like a record sleeve's liner notes rather than a dashboard: the
 * newest album set large, the account's figures as a ruled ledger, and the
 * saves before it as a numbered index. The artwork and the type carry the page;
 * there are no tiles, glows or greeting to get past first.
 */
export const HomePage = () => {
  const { t } = useTranslation();
  const profile = useAccountProfile();
  const library = useCloudLibrary();
  const devices = useDevices();

  const projects = useMemo(
    () => (library.data ? selectLiveProjects(library.data.projects) : []),
    [library.data],
  );
  const tracks = useMemo(
    () => (library.data ? collectCatalogTracks(library.data) : []),
    [library.data],
  );
  const duplicateGroups = useMemo(() => buildDuplicateGroups(tracks), [tracks]);
  const duplicatesByProject = useMemo(
    () => countDuplicatesByProject(duplicateGroups),
    [duplicateGroups],
  );
  const runningTime = useMemo(
    () => sumTrackDuration(projects.flatMap((project) => project.tracks)),
    [projects],
  );

  const [latest, ...earlier] = projects.slice(0, RECENT_PROJECT_COUNT);
  const shelfCount = library.data?.shelves.filter((shelf) => shelf.deletedAt === null).length ?? 0;
  const name = profile.data?.displayName || profile.data?.username;

  return (
    <AppPage
      width="full"
      header={
        <PageHeader
          eyebrow={name ? t("home.signedInAs", { name }) : undefined}
          title={t("home.title")}
          actions={
            // A phone already has Catalog and Shelves as tabs directly above this
            // header, and `PageHeader` keeps its actions on one unshrinking row, so
            // repeating them there squeezed the title into a column two words wide.
            <div className="hidden items-center gap-2 sm:flex">
              <Button asChild variant="ghost">
                <Link to={ROUTES.shelves}>{t("home.shelvesAction")}</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to={ROUTES.catalog}>
                  {t("home.catalogAction")}
                  <ArrowRight />
                </Link>
              </Button>
            </div>
          }
        />
      }
    >
      <div className="flex flex-col gap-10 pb-10">
        {library.isPending && <Skeleton className="h-[28rem] w-full" />}
        {library.isError && <StatusMessage tone="error">{t("catalog.unavailable")}</StatusMessage>}

        {library.data && latest === undefined && (
          <EmptyState
            icon={<ListMusic />}
            title={t("catalog.empty.title")}
            description={t("catalog.empty.description")}
          />
        )}

        {latest !== undefined && (
          <LatestProject
            duplicateCount={duplicatesByProject.get(latest.desktopProjectId) ?? 0}
            project={latest}
          />
        )}

        {library.data && (
          <LibraryLedger
            deviceCount={devices.data?.devices.length}
            deviceLimit={devices.data?.deviceLimit}
            duplicateGroupCount={duplicateGroups.length}
            projectCount={projects.length}
            runningTime={runningTime}
            shelfCount={shelfCount}
            storageLimitBytes={library.data.storageLimitBytes}
            storedBytes={library.data.storedBytes}
            trackCount={tracks.length}
          />
        )}

        {earlier.length > 0 && (
          <PageSection
            title={t("home.recent.title")}
            actions={
              projects.length > RECENT_PROJECT_COUNT ? (
                <Button asChild variant="ghost" size="sm">
                  <Link to={ROUTES.catalog}>
                    {t("home.recent.viewAll")}
                    <ArrowRight />
                  </Link>
                </Button>
              ) : undefined
            }
          >
            <ol className="grid border-b border-border lg:grid-cols-2 lg:gap-x-12">
              {earlier.map((project, index) => (
                <li key={project.desktopProjectId} className="min-w-0">
                  {/* The latest album is number one; the index continues from it. */}
                  <RecentProjectRow project={project} rank={index + 2} />
                </li>
              ))}
            </ol>
          </PageSection>
        )}
      </div>
    </AppPage>
  );
};
